import React, { useEffect } from 'react';
import { Modal } from '../../elements/Modal';
import IngredientCard from './IngredientCard';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../elements/icon';
import { isGermanLanguage } from '../../../utils/language';
import { BackToThePage, Close, IncludesCarousel, NutrientsCarousel } from '../../../Constants';

const IngredientsModal = ({
  open,
  handleClose,
  data,
  allIngredients,
  name,
  focusAreaCount = 0,
  personalSignalsCount = 0,
  specialConditionsCount = 0,
  medicationsCount = 0,
  showScientificName = true,
  onOpenEdit,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      className="t-w-full t-max-w-[95vw] sm:t-max-w-[500px] t-h-[85vh] !t-px-0 t-overflow-hidden t-flex t-flex-col"
      childrenClass="!t-h-full !t-flex !t-flex-col !t-items-stretch !t-justify-start !t-gap-0 !t-overflow-hidden"
      notCrossIcon
      enableClickOutsideClose={true}
    >
      {/* Header */}
      <div className="t-flex-shrink-0 t-w-full t-flex t-items-center t-justify-between t-px-3 t-py-3 t-bg-navy t-z-10">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}
          className="t-flex t-items-center t-gap-1 t-cursor-pointer hover:t-opacity-70 t-bg-transparent t-border-0 t-p-0 t-font-sans t-text-[13px] t-font-medium t-text-surface"
        >
          <Icon name="chevronLeft" size={24} color="#F9F9F9" raw />
          <span className="t-text-surface t-text-sm">{t(BackToThePage)}</span>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}
          className="t-flex t-items-center t-gap-1 t-cursor-pointer hover:t-opacity-70 t-bg-transparent t-border-0 t-p-0 t-font-sans t-text-[13px] t-font-medium t-text-surface"
        >
          <span className="t-text-surface t-text-sm">{t(Close)}</span>
          <Icon name="cross" size={24} color="#F9F9F9" raw />
        </button>
      </div>

      {/* AI context banner */}
      <div className="t-flex-shrink-0 t-bg-quaternary/10 t-border-b t-border-quaternary/20 t-px-4 t-py-3">
        <p className="!t-m-0 !t-mb-1 t-text-xs t-font-bold t-text-quaternary">{t('IngredientsModal_BasedOnQuizAnswers')}</p>
        <p className="!t-m-0 t-text-xs t-text-navy t-leading-relaxed">
          {focusAreaCount > 0 && personalSignalsCount > 0
            ? (() => {
                const parts = [
                  { count: focusAreaCount, singular: 'Carousel_HealthGoalSingular', plural: 'Carousel_HealthGoalPlural' },
                  { count: personalSignalsCount, singular: 'Carousel_PersonalSignalSingular', plural: 'Carousel_PersonalSignalPlural' },
                ];
                if (specialConditionsCount > 0) parts.push({ count: specialConditionsCount, singular: 'Carousel_SpecialConditionSingular', plural: 'Carousel_SpecialConditionPlural' });
                if (medicationsCount > 0) parts.push({ count: medicationsCount, singular: 'Carousel_MedicationSingular', plural: 'Carousel_MedicationPlural' });
                return (
                  <>
                    {t('Carousel_BuiltAroundPrefix')}{' '}
                    {parts.map((p, i) => {
                      const sep = i === 0 ? '' : i === parts.length - 1 ? ` ${t('Carousel_And')} ` : ', ';
                      return (
                        <React.Fragment key={p.singular}>
                          {sep}
                          <strong>{p.count} {p.count === 1 ? t(p.singular) : t(p.plural)}</strong>
                        </React.Fragment>
                      );
                    })}
                    . {t('Carousel_EachIngredientReason')}
                  </>
                );
              })()
            : t('IngredientsModal_SubText')}
        </p>
      </div>

      <div className="t-flex-1 t-overflow-y-auto t-overflow-x-hidden hide-scrollbar t-min-h-0">
        {/* Title row */}
        <div className="t-px-4 t-pt-4 t-pb-2 t-flex t-items-center t-justify-between">
          <div>
            <h1 className="!t-m-0 t-font-serif t-font-normal t-text-navy !t-text-[22px]">
              {name}
            </h1>
            <p className="!t-m-0 t-font-sans t-text-sm t-text-navy/70">
              {t(IncludesCarousel)} {data?.length} {t(NutrientsCarousel)}
            </p>
          </div>
        </div>

        {/* Ingredient cards */}
        <div className="t-px-4 t-pb-4">
          <div className="t-flex t-items-center t-gap-3 t-px-3 t-pb-1.5">
            <div className="t-w-2.5" />
            <div className="t-flex-1" />
            <div className="t-flex t-items-center t-gap-2 t-flex-shrink-0">
              <span className="t-text-[10px] t-font-bold t-text-navy/40 t-uppercase t-tracking-wider t-min-w-[44px] t-text-right">{t('Quantity')}</span>
              <span className="t-text-[10px] t-font-bold t-text-navy/40 t-uppercase t-tracking-wider t-min-w-[28px] t-text-right">{isGermanLanguage(i18n.language) ? t('OrderDetails_Col_NRV') : '%NRV'}</span>
              <span className="t-w-2.5" />
            </div>
          </div>
          {isLoading && (
            <p role="status" className="t-text-sm t-text-teal-muted">
              {t('Dashboard_IngredientsModal_Loading')}
            </p>
          )}
          {!isLoading && data?.map((item, i) => {
            const ingredient = allIngredients?.data?.find(
              (ing) => ing.ingredient === item?.ingredient || ing.ingredient_de === item?.ingredient
            );
            const ingredientData = ingredient;
            return (
              <IngredientCard
                key={'modal-card-' + item.ingredient + '-' + i}
                item={item}
                index={i}
                ingredientData={ingredientData}
                defaultOpen={i === 0}
                showScientificName={showScientificName}
              />
            );
          })}
        </div>

        {/* Edit formula CTA */}
        {onOpenEdit && (
          <div className="t-px-4 t-pb-4 t-text-center t-border-t t-border-g200 t-pt-4">
            <p className="!t-m-0 t-text-xs t-text-navy/70 t-mb-2">{t('IngredientsModal_NotHappy')}</p>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenEdit();
              }}
              className="t-bg-transparent t-border-0 t-text-sm t-font-semibold t-text-navy t-underline t-underline-offset-2 t-cursor-pointer"
            >
              {t('IngredientsModal_EditFormula')}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default IngredientsModal;
